import React, { useEffect, useState, createElement } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DollarSignIcon, PlusIcon, AlertTriangleIcon, CalendarIcon } from 'lucide-react';
import { calculateValuation, QuizData, ValuationResult } from '../utils/ValuationCalculator';
import { SchedulerEmbed } from './SchedulerEmbed';
import { sendToGoHighLevel } from '../utils/ghlIntegration';
import { getQuizData } from '../utils/quizDataPersistence';
// Mock data for preview mode
const PREVIEW_DATA: QuizData = {
  isOwner: 'yes',
  state: 'TX',
  revenue: '3-5M',
  profit: '15-25',
  locations: '2-3',
  providers: '4-7',
  payerMix: 'mix',
  concentration: 'no',
  trend: 'up-10-20',
  timeline: '12-24m',
  ownerHours: '<20'
};
interface ResultsPageProps {
  preview?: boolean;
}
export function ResultsPage({
  preview = false
}: ResultsPageProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [result, setResult] = useState<ValuationResult | null>(null);
  // Determine if we're in preview mode based on URL path or prop
  const isPreviewMode = preview || location.pathname.includes('/preview');
  // Try to get form data from navigation state first, then fall back to localStorage
  // In preview mode, use mock data
  const formData = isPreviewMode ? {
    ...PREVIEW_DATA,
    firstName: 'Preview',
    email: 'preview@example.com'
  } : location.state as QuizData & {
    firstName: string;
    email: string;
  } || getQuizData() as any;
  useEffect(() => {
    // In preview mode, skip the redirect check
    if (!isPreviewMode) {
      // If no quiz data available at all, redirect to start
      if (!formData || !formData.revenue) {
        console.warn('No quiz data found, redirecting to start');
        navigate('/');
        return;
      }
    }
    console.log(isPreviewMode ? 'Preview Mode - Results Viewed' : 'ResultsViewed', formData);
    // Calculate valuation from quiz data
    const calculatedResult = calculateValuation(formData as QuizData);
    setResult(calculatedResult);
    // Only send to GHL if NOT in preview mode and contact info is available
    if (!isPreviewMode && formData.email) {
      sendToGoHighLevel({
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        email: formData.email,
        phone: formData.mobile || '',
        smsConsent: formData.smsConsent || false,
        isOwner: formData.isOwner,
        state: formData.state,
        revenue: formData.revenue,
        profit: formData.profit,
        locations: formData.locations,
        providers: formData.providers,
        ownerHours: formData.ownerHours,
        payerMix: formData.payerMix,
        concentration: formData.concentration,
        trend: formData.trend,
        timeline: formData.timeline,
        valuationLow: calculatedResult.low,
        valuationHigh: calculatedResult.high,
        multipleRange: `${Math.round(calculatedResult.multipleRange[0])}x-${Math.round(calculatedResult.multipleRange[1])}x`,
        qualityScore: calculatedResult.qualityScore,
        submittedAt: new Date().toISOString(),
        source: 'valuation_calculator_results'
      }).catch(err => {
        console.error('GHL results submission error (non-blocking):', err);
      });
    }
  }, [formData, navigate, isPreviewMode]);
  useEffect(() => {
    // Load Wistia scripts
    const script1 = document.createElement('script');
    script1.src = 'https://fast.wistia.com/player.js';
    script1.async = true;
    document.body.appendChild(script1);
    const script2 = document.createElement('script');
    script2.src = 'https://fast.wistia.com/embed/n4ae7z485x.js';
    script2.async = true;
    script2.type = 'module';
    document.body.appendChild(script2);
    return () => {
      document.body.removeChild(script1);
      document.body.removeChild(script2);
    };
  }, []);
  const scrollToCalendar = () => {
    const calendarSection = document.getElementById('calendar-section');
    if (calendarSection) {
      calendarSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  if (!result) {
    return <div className="min-h-screen bg-white flex items-center justify-center" data-id="element-178">
        <div className="text-center" data-id="element-179">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4" data-id="element-180"></div>
          <p className="text-gray-600" data-id="element-181">Calculating your valuation...</p>
        </div>
      </div>;
  }
  const formatCurrency = (value: number) => {
    // Round to nearest hundred thousand to avoid overly precise numbers
    const roundedValue = Math.round(value / 100000) * 100000;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(roundedValue);
  };
  const showScheduler = formData.isOwner === 'yes';
  const getKeyManRiskExplanation = () => {
    if (result.keyManRisk === 'High') {
      return 'Because you handle most patient visits, your value is reduced to reflect owner dependency. Training associates and reducing your clinical hours could add significant value.';
    } else if (result.keyManRisk === 'Moderate') {
      return 'Your clinic has some owner dependency. Delegating more patient care to associates could increase your multiple.';
    } else {
      return 'Your clinic operates well without heavy owner involvement, which increases its value to buyers.';
    }
  };
  return <div className="min-h-screen bg-white py-6 md:py-12 px-4" data-id="element-182">
      {/* Logo */}
      <div className="max-w-4xl mx-auto mb-6" data-id="element-183">
        <div className="animate-fade-in" data-id="element-184">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qmxPrQcAoJQeBtBGYhM9D6/516lion.png" alt="516 Partners Logo" className="h-16 md:h-24 mx-auto" data-id="element-185" />
        </div>
      </div>

      {/* Preview Mode Banner */}
      {isPreviewMode && <div className="max-w-4xl mx-auto mb-4" data-id="element-186">
          <div className="bg-yellow-100 border-2 border-yellow-500 p-4 text-center" data-id="element-187">
            <p className="text-yellow-900 font-semibold" data-id="element-188">
              📋 PREVIEW MODE - This is a sample results page with mock data.
              Changes made here will appear on the live results page.
            </p>
          </div>
        </div>}

      <div className="max-w-4xl mx-auto space-y-8 md:space-y-12" data-id="element-189">
        {/* Results Header */}
        <div className="bg-white border border-gray-200 p-4 sm:p-6 md:p-8 lg:p-12 animate-fade-in-scale" data-id="element-190">
          <div className="text-center mb-8 md:mb-12 animate-fade-in" data-id="element-191">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-3 md:mb-4 leading-tight" data-id="element-192">
              Congratulations! Here's How Much Your Clinic Could Be Worth...
            </h1>
            <p className="text-base sm:text-lg font-bold text-gray-600" data-id="element-193">
              Step 1: Watch the video to understand your results
            </p>
          </div>

          {/* Wistia Video */}
          <div className="mb-8 md:mb-12 animate-stagger-in animate-delay-100" data-id="element-194">
            <style dangerouslySetInnerHTML={{
            __html: `
                  wistia-player[media-id='n4ae7z485x']:not(:defined) { 
                    background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/n4ae7z485x/swatch'); 
                    display: block; 
                    filter: blur(5px); 
                    padding-top: 56.25%; 
                  }
                `
          }} data-id="element-195" />
            <wistia-player media-id="n4ae7z485x" aspect="1.7777777777777777" style={{
            width: '100%',
            display: 'block'
          }} data-id="element-196" />
          </div>

          {/* Step 2 Header & Book Call Button */}
          <div className="mb-8 md:mb-12 animate-stagger-in animate-delay-200" data-id="element-197">
            <div className="mb-4 text-center" data-id="element-198">
              <h2 className="text-base sm:text-lg font-bold text-gray-600" data-id="element-199">
                Step 2: Book your FREE review call to unlock a more crystal
                clear range
              </h2>
            </div>

            {/* Book Call Button */}
            <div className="text-center" data-id="element-200">
              <button onClick={scrollToCalendar} className="inline-flex items-center space-x-2 px-8 sm:px-10 py-4 bg-yellow-500 text-gray-900 font-bold text-base sm:text-lg hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-201">
                <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-202" />
                <span data-id="element-203">Book Your Review Call</span>
              </button>
            </div>
          </div>

          {/* Valuation Range - Moved here after video and button */}
          <div className="bg-gray-50 border-2 border-gray-900 p-6 sm:p-8 mb-6 md:mb-8 animate-stagger-in animate-delay-300" data-id="element-204">
            <p className="text-xs sm:text-sm font-medium text-gray-600 uppercase tracking-wide mb-2 text-center" data-id="element-205">
              Your Clinic's Range
            </p>
            <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 text-center whitespace-nowrap" data-id="element-206">
              {formatCurrency(result.low)} – {formatCurrency(result.high)}
            </div>
            <p className="text-xs sm:text-sm text-gray-600 text-center" data-id="element-207">
              Based on your answers. Not an appraisal.
            </p>
          </div>

          {/* Key Man Risk Alert */}
          {result.keyManRisk === 'High' && <div className="mb-6 md:mb-8 p-4 sm:p-6 bg-yellow-50 border border-yellow-600 animate-stagger-in animate-delay-400" data-id="element-208">
              <div className="flex items-start space-x-3" data-id="element-209">
                <AlertTriangleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 flex-shrink-0 mt-1" data-id="element-210" />
                <div data-id="element-211">
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base" data-id="element-212">
                    Owner Dependency Impact
                  </h3>
                  <p className="text-gray-700 text-sm sm:text-base" data-id="element-213">
                    {getKeyManRiskExplanation()}
                  </p>
                </div>
              </div>
            </div>}

          {/* How We Calculated Your Range */}
          <div className="mb-6 md:mb-8 animate-stagger-in animate-delay-500" data-id="element-214">
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-gray-900 mb-2 text-center" data-id="element-215">
              How It Was Calculated...
            </h2>
            <p className="text-sm text-gray-600 text-center mb-6" data-id="element-216">
              These are extremely rough estimates, not exact numbers.
            </p>

            <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6" data-id="element-217">
              {/* EBITDA */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-200 gap-4 animate-stagger-in animate-delay-100" data-id="element-218">
                <div className="flex items-center space-x-2 sm:space-x-3 min-w-0" data-id="element-219">
                  <DollarSignIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0" data-id="element-220" />
                  <span className="text-gray-900 font-medium text-sm sm:text-base truncate" data-id="element-221">
                    Annual Profit (EBITDA)
                  </span>
                </div>
                <span className="text-gray-900 font-bold text-sm sm:text-base whitespace-nowrap" data-id="element-222">
                  {formatCurrency(result.ebitda)}
                </span>
              </div>

              {/* Multiple */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-200 gap-4 animate-stagger-in animate-delay-200" data-id="element-223">
                <span className="text-gray-900 font-medium text-sm sm:text-base min-w-0 truncate" data-id="element-224">
                  Market Multiple Applied
                </span>
                <span className="text-gray-900 font-bold text-sm sm:text-base whitespace-nowrap" data-id="element-225">
                  {Math.round(result.multipleRange[0])}x–
                  {Math.round(result.multipleRange[1])}x
                </span>
              </div>

              {/* Result */}
            </div>

            {/* Book Call Button - After Calculation */}
            <div className="text-center mt-8" data-id="element-226">
              <button onClick={scrollToCalendar} className="inline-flex items-center space-x-2 px-8 sm:px-10 py-4 bg-yellow-500 text-gray-900 font-bold text-base sm:text-lg hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-227">
                <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-228" />
                <span data-id="element-229">Book Your Review Call</span>
              </button>
            </div>
          </div>

          {/* Value Unlocks */}
          <div className="mb-6 md:mb-8 animate-stagger-in animate-delay-600" data-id="element-230">
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-gray-900 mb-4" data-id="element-231">
              Quick wins we see next:
            </h2>
            <div className="space-y-3" data-id="element-232">
              <div className="flex items-start space-x-3 p-3 sm:p-4 border border-gray-300 animate-stagger-in animate-delay-100" data-id="element-233">
                <span className="text-gray-900 font-bold flex-shrink-0" data-id="element-234">•</span>
                <p className="text-gray-900 text-sm sm:text-base" data-id="element-235">
                  Find hidden expenses that can increase your value
                </p>
              </div>
              <div className="flex items-start space-x-3 p-3 sm:p-4 border border-gray-300 animate-stagger-in animate-delay-200" data-id="element-236">
                <span className="text-gray-900 font-bold flex-shrink-0" data-id="element-237">•</span>
                <p className="text-gray-900 text-sm sm:text-base" data-id="element-238">
                  Reduce dependence on any single insurance or referral source
                </p>
              </div>
              <div className="flex items-start space-x-3 p-3 sm:p-4 border border-gray-300 animate-stagger-in animate-delay-300" data-id="element-239">
                <span className="text-gray-900 font-bold flex-shrink-0" data-id="element-240">•</span>
                <p className="text-gray-900 text-sm sm:text-base" data-id="element-241">
                  Improve profit margins in ways buyers care about
                </p>
              </div>
            </div>

            {/* Book Call Button - After Quick Wins */}
            <div className="text-center mt-8" data-id="element-242">
              <button onClick={scrollToCalendar} className="inline-flex items-center space-x-2 px-8 sm:px-10 py-4 bg-yellow-500 text-gray-900 font-bold text-base sm:text-lg hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-243">
                <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-244" />
                <span data-id="element-245">Book Your Review Call</span>
              </button>
            </div>
          </div>
        </div>

        {/* Calendar/Scheduler - Simplified */}
        <section id="calendar-section" className="mb-32 pb-32" data-id="element-246">
          <h2 className="text-3xl font-serif font-bold text-center mb-8 text-gray-900" data-id="element-247">
            Schedule Your 15 Min Clinic Review Call
          </h2>
          {showScheduler ? <div className="bg-white pb-32" data-id="element-248">
              <iframe src="https://api.leadconnectorhq.com/widget/booking/ul1jgYvunxHt2TjeNpFB" style={{
            width: '100%',
            border: 'none',
            minHeight: '1200px',
            height: '1200px'
          }} scrolling="yes" id="ul1jgYvunxHt2TjeNpFB_1762398344212" title="Book Your Call" data-id="element-249" />
            </div> : <div className="bg-gray-50 border border-gray-300 p-6 sm:p-8 text-center" data-id="element-250">
              <h3 className="text-lg sm:text-xl font-serif font-bold text-gray-900 mb-3" data-id="element-251">
                Want Expert Guidance?
              </h3>
              <p className="text-gray-600 mb-6 text-sm sm:text-base" data-id="element-252">
                Learn how to maximize your clinic's value before selling
              </p>
              <button onClick={() => alert('Learn more content would be sent')} className="px-6 sm:px-8 py-3 bg-yellow-500 text-gray-900 font-semibold hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-colors text-sm sm:text-base" data-id="element-253">
                Learn More
              </button>
            </div>}
        </section>
      </div>
    </div>;
}